import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';

const NAVY = '#0D1B2A';
const AMBER = '#F5A623';

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = src;
  });
}

async function cropToSquareBlob(imageSrc, cropPixels, outputType) {
  const img = await loadImage(imageSrc);
  const size = 600; // fixed output square, plenty for avatar/logo display sizes
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, size, size,
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), outputType || 'image/jpeg', 0.92);
  });
}

// Shared circular-crop step shown after a file is selected, before it's uploaded.
// Every avatar/logo in the app renders as a circle via CSS border-radius, so this
// outputs a plain square image (no alpha mask needed) and lets the crop UI show the
// circular guide for correct framing.
export default function ImageCropModal({ file, onCancel, onConfirm }) {
  const { t } = useTranslation();
  const [imageSrc] = useState(() => URL.createObjectURL(file));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedPixels || saving) return;
    setSaving(true);
    const outputType = file.type === 'image/png' || file.type === 'image/gif' ? 'image/png' : 'image/jpeg';
    const blob = await cropToSquareBlob(imageSrc, croppedPixels, outputType);
    URL.revokeObjectURL(imageSrc);
    setSaving(false);
    onConfirm(blob, outputType);
  };

  const handleCancel = () => {
    URL.revokeObjectURL(imageSrc);
    onCancel();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(13,27,42,0.72)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 3000, padding: '1rem',
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '420px',
        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ padding: '1.1rem 1.25rem 0.75rem', fontWeight: 700, color: NAVY, fontSize: '1.05rem' }}>
          {t('imageCropModal.title')}
        </div>
        <div style={{ position: 'relative', width: '100%', height: '340px', backgroundColor: '#111' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div style={{ padding: '1rem 1.25rem' }}>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: NAVY, display: 'block', marginBottom: '6px' }}>
            {t('imageCropModal.zoomLabel')}
          </label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: '100%' }}
          />
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.1rem' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              style={{
                flex: 1, padding: '0.7rem', borderRadius: '10px', border: '1.5px solid #E2E6EB',
                backgroundColor: '#fff', color: NAVY, fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
              }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={saving}
              style={{
                flex: 1, padding: '0.7rem', borderRadius: '10px', border: 'none',
                backgroundColor: AMBER, color: NAVY, fontWeight: 700, cursor: saving ? 'default' : 'pointer',
                fontSize: '0.9rem', opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
